/**
 * Guarded browser storage for CSV mapping profiles.
 *
 * Behavior contract (every failure leaves the converter usable):
 *
 * - **SSR**: `window` is never touched when it is undefined — resolution
 *   returns `null` and loads read as empty.
 * - **Disabled storage** (blocked cookies, SecurityError on access): reads as
 *   empty, writes report `unavailable`. Nothing throws.
 * - **Malformed JSON / unsupported versions**: the unparseable payload (load)
 *   or entries (list parse in `@/lib/csv-mapping-profiles`) are discarded;
 *   loads read as empty, valid entries survive.
 * - **Quota full**: writes report `quota` and keep the previous payload (a
 *   failed `setItem` never partially writes). Nothing throws.
 * - **Bounds**: writes past `MAX_CSV_PROFILES`, or a profile serializing past
 *   `MAX_CSV_PROFILE_JSON_BYTES`, are refused with `too-many` / `too-large`
 *   before touching storage.
 *
 * Profiles store mapping configuration only — never statement balances,
 * statement dates, CSV rows or emitted ledger text (enforced by
 * `@/lib/csv-mapping-profiles`).
 */

import {
  MAX_CSV_PROFILES,
  MAX_CSV_PROFILE_JSON_BYTES,
  parseMappingProfileList,
  serializeMappingProfiles,
  utf8ByteLength,
  type CsvMappingProfile,
} from "@/lib/csv-mapping-profiles";

/** The only key profiles live under — versioned with the schema. */
export const CSV_PROFILES_STORAGE_KEY = "beancount.csv-mapping-profiles.v1";

/**
 * The `Storage` surface this adapter needs, structural so tests can inject a
 * fake and callers never touch `window.localStorage` directly.
 */
export interface ProfileStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type SaveProfilesReason =
  | "unavailable"
  | "quota"
  | "too-many"
  | "too-large";

export type SaveProfilesResult =
  | { ok: true }
  | { ok: false; reason: SaveProfilesReason };

/**
 * The translated notice for each refusal — shared by Save/Update/Delete and
 * the file-import commit so one failure reads the same wherever it happens.
 */
export const SAVE_PROFILES_ERROR_KEYS: Record<SaveProfilesReason, string> = {
  unavailable: "tools.csvWidget.profiles.errors.unavailable",
  quota: "tools.csvWidget.profiles.errors.quota",
  "too-many": "tools.csvWidget.profiles.errors.tooMany",
  "too-large": "tools.csvWidget.profiles.errors.tooLarge",
};

function isQuotaError(error: unknown): boolean {
  if (!isRecord(error)) return false;
  if (error.name === "QuotaExceededError") return true;
  // Legacy WebKit / Firefox codes for the same condition.
  return error.code === 22 || error.code === 1014;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * The backing store, or `null` outside a browser / when access is blocked.
 * The `typeof window` check comes first so SSR never touches `window`; the
 * `try` covers browsers where merely reading `localStorage` throws.
 */
export function resolveProfileStorage(): ProfileStorage | null {
  if (typeof window === "undefined") return null;
  try {
    const storage = window.localStorage;
    if (storage === null || storage === undefined) return null;
    return storage;
  } catch {
    return null;
  }
}

export function isProfileStorageAvailable(
  storage: ProfileStorage | null = resolveProfileStorage(),
): boolean {
  return storage !== null;
}

/**
 * Load saved profiles. Any failure — no store, unreadable payload, unknown
 * versions — reads as an empty list (unknown-version entries are dropped by
 * the list parser, valid ones survive).
 */
export function loadMappingProfiles(
  storage: ProfileStorage | null = resolveProfileStorage(),
): CsvMappingProfile[] {
  if (storage === null) return [];
  let raw: string | null;
  try {
    raw = storage.getItem(CSV_PROFILES_STORAGE_KEY);
  } catch {
    return [];
  }
  if (raw === null || raw === "") return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  return parseMappingProfileList(parsed);
}

/**
 * Persist profiles, enforcing the collection bounds first so a refusal never
 * disturbs what is already stored. Never throws: every storage failure is a
 * `{ ok: false }` the converter UI can ignore or surface.
 */
export function saveMappingProfiles(
  profiles: CsvMappingProfile[],
  storage: ProfileStorage | null = resolveProfileStorage(),
): SaveProfilesResult {
  if (storage === null) return { ok: false, reason: "unavailable" };
  if (profiles.length > MAX_CSV_PROFILES) {
    return { ok: false, reason: "too-many" };
  }
  for (const profile of profiles) {
    if (
      utf8ByteLength(JSON.stringify(profile)) > MAX_CSV_PROFILE_JSON_BYTES
    ) {
      return { ok: false, reason: "too-large" };
    }
  }
  try {
    storage.setItem(CSV_PROFILES_STORAGE_KEY, serializeMappingProfiles(profiles));
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: isQuotaError(error) ? "quota" : "unavailable" };
  }
}
